import type React from "react";

import { useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import { DownloadingCsv } from "app/pages/sales-intelligence/modules/enrichment/components/DownloadingCsv/DownloadingCsv";
import { ErrorView } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Error";
import EnrichmentError from "app/pages/sales-intelligence/modules/enrichment/components/EnrichmentError/EnrichmentError";
import { PreparingCsv } from "app/pages/sales-intelligence/modules/enrichment/components/PreparingCsv/PreparingCsv";
import type { EnrichmentModalStage } from "app/pages/sales-intelligence/modules/enrichment/components/type";
import { EnrichmentCsvContainer } from "app/pages/sales-intelligence/modules/enrichment/containers/EnrichmentCsvContainer/EnrichmentCsvContainer";
import UploadCSVEnrichmentContainer from "app/pages/sales-intelligence/modules/enrichment/containers/UploadCSVEnrichmentContainer/UploadCSVEnrichmentContainer";
import { useEnrichmentCountries } from "app/pages/sales-intelligence/modules/enrichment/hooks/useEnrichmentCountries";
import { enrichmentQueryKeys } from "app/pages/sales-intelligence/modules/enrichment/queries/key-factory/queryKeyFactory";
import { createStaticListThunk } from "app/pages/sales-intelligence/modules/enrichment/store/effects/enrichment";
import { selectParsed, selectIsEnriching } from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type { PostCsvProps } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { redirectToStaticList } from "app/pages/sales-intelligence/services/redirectService";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { fetchWorkspacesThunk } from "app/pages/sales-intelligence/sub-modules/common/store/effects";

type ModalContentProps = {
    onConfirm({ columns, country, domainFieldName }: PostCsvProps): void;
    onReplace(): void;
    onClose(): void;
    onFileChange(event: React.ChangeEvent<HTMLInputElement>): void;
    setSelectedHeader(header: string): void;
    selectedHeader: string;
    fileName: string;
    modalStage: EnrichmentModalStage;
    errorText: string;
};

export const ModalContent = ({
    onConfirm,
    onReplace,
    setSelectedHeader,
    onFileChange,
    onClose,
    selectedHeader,
    fileName,
    modalStage,
    errorText,
}: ModalContentProps) => {
    const parsingResult = useSelector(selectParsed);

    const isEnriching = useSelector(selectIsEnriching);

    const dispatch = useDispatch();
    const { localCountry } = useEnrichmentCountries();

    const queryClient = useQueryClient();

    const showToast = (text: string, label: string, params: { id: string }) => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text,
                    linkText: label,
                    onClick: () => redirectToStaticList(params),
                }),
            ),
        );
    };

    const onSave = async () => {
        if (selectedHeader) {
            const domains = parsingResult?.allDomainValues[selectedHeader];
            if (!domains) {
                return;
            }

            const opportunity = await dispatch(createStaticListThunk(domains, fileName, localCountry.id));
            await dispatch(fetchWorkspacesThunk());
            void queryClient.invalidateQueries({ queryKey: enrichmentQueryKeys.lists() });
            showToast(
                translate("si.enrichment.toast.success.message"),
                translate("si.enrichment.toast.success.view-list.label"),
                {
                    id: opportunity.opportunitiesList.opportunityListId,
                },
            );
            enrichmentTrackingService.csv.company.trackCreateStaticList(domains.length);
            onClose();
        }
    };

    const handleCloseModal = () => {
        enrichmentTrackingService.csv.company.trackCancelEnrichmentClosePopup();
        onClose();
    };

    const discardStaticList = () => {
        const domains = parsingResult?.allDomainValues[selectedHeader];
        void queryClient.invalidateQueries({ queryKey: enrichmentQueryKeys.lists() });

        enrichmentTrackingService.csv.company.discardStaticList(domains?.length ?? 0);
        onClose();
    };

    const modalContent = {
        upload: (
            <UploadCSVEnrichmentContainer
                onClose={handleCloseModal}
                onFileChange={onFileChange}
            />
        ),
        enrichment: (
            <EnrichmentCsvContainer
                setSelectedHeader={setSelectedHeader}
                onClose={onClose}
                onConfirm={onConfirm}
                onReplace={onReplace}
                parsingResult={parsingResult}
                selectedHeader={selectedHeader}
                fileName={fileName}
                isEnriching={isEnriching}
            />
        ),
        error: (
            <EnrichmentError
                onClose={onClose}
                errorText={errorText}
            />
        ),
        preparing: <PreparingCsv />,
        downloading: (
            <DownloadingCsv
                onCancel={discardStaticList}
                onSave={onSave}
            />
        ),
    };

    return (
        <ErrorBoundary FallbackComponent={() => <ErrorView onClose={onClose} />}>
            {modalContent[modalStage]}
        </ErrorBoundary>
    );
};
